import { memo, useCallback } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import {
  Form,
  FormInput,
  useFetch,
  useNotificationContext,
} from "@4uhub/lib4uhub";
import { IGetAllUsers } from "../../../models/users";
import { updateUserPin } from "../../../services/userPin.service";
import { TUserChangePinForm, userChangePinSchema } from "./UserChangePinSchema";

const translationPath = "page.register.users.change_pin.";

interface IUserChangePinModalProps {
  user: IGetAllUsers;
  onClose: () => void;
}

export const UserChangePinModal = memo(
  ({ user, onClose }: IUserChangePinModalProps) => {
    const { t } = useTranslation();
    const { setMessage } = useNotificationContext();
    const { sendRequest, loading } = useFetch(updateUserPin);

    const methods = useForm<TUserChangePinForm>({
      resolver: zodResolver(userChangePinSchema),
      defaultValues: { pin: "" },
    });

    const onSubmit = useCallback(
      async (values: TUserChangePinForm) => {
        if (!user?.id) return;

        const { success } = await sendRequest({
          userId: user.id,
          newPin: values.pin,
        });

        if (success) {
          setMessage({
            message: t(translationPath + "success"),
            type: "success",
            notificationKey: new Date().getMilliseconds(),
          });
          onClose();
        }
      },
      [sendRequest, user, setMessage, t, onClose],
    );

    return (
      <Form {...methods}>
        <Box
          component="form"
          autoComplete="off"
          onSubmit={methods.handleSubmit(onSubmit)}
          sx={{ width: "100%", maxWidth: 420 }}
        >
          <Grid container spacing={2} justifyContent="center">
            <Grid item xs={12}>
              <Typography variant="h6" textAlign="center" lineHeight={1.3}>
                {t(translationPath + "title", { username: user?.userName })}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <FormInput
                size="small"
                name="pin"
                fullWidth
                type="password"
                maxLength={4}
                label={t(translationPath + "pin") || "PIN"}
                inputProps={{
                  inputMode: "numeric",
                  autoComplete: "one-time-code",
                  autoFocus: true,
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Stack direction="row" spacing={2}>
                <Button
                  variant="outlined"
                  fullWidth
                  size="small"
                  onClick={onClose}
                  disabled={loading}
                >
                  {t(translationPath + "cancel")}
                </Button>
                <Button
                  variant="contained"
                  fullWidth
                  size="small"
                  type="submit"
                  disabled={loading}
                >
                  {loading ? (
                    <CircularProgress size={18} />
                  ) : (
                    t(translationPath + "confirm")
                  )}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </Form>
    );
  },
);

export default UserChangePinModal;
